﻿import axios from "@/features/axiosHelper";
import { createAlert } from "@/features/homepage/alertSliceList";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  loadSchemaCollection,
  setSchemaCollectionAdded,
  setIsLoadCollectionClicked,
  setTriggerRefreshForConnectionSummary,
  clearSharedCatalogSetupDetails,
  clearConnectionPairObject,
} from "@/features/connections/integrationSlice";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";

export const editExistingConnection = async (
  params,
  dispatch,
  onSuccess,
  onError
) => {
  dayjs.extend(timezone);
  try {
    const response = await axios.put("/connections/edit-connection", params, {
      params: { localTimeZone: dayjs.tz.guess() },
    });
    if (response && response.data) {
      dispatch(setTriggerRefreshForConnectionSummary());
      onSuccess();
    } else {
      onError("Partial update. Please refresh and try again.");
    }
  } catch (error) {
    console.error("Error updating connection:", error);
    onError("An error occurred while updating the connection.");
  }
};

export const buildEditModel = (
  connectionId,
  connectionDetails,
  modifiedConnectionPairs,
  catalogSharingSetupDetails,
  connectionPairObject,
  workflowModel,
  endpointId
) => {
  const model = {
    ConnectionId: connectionId,
    ConnectionName: connectionDetails.connectionName,
    DisplayName: connectionDetails.displayName,
    EndpointId: endpointId ?? 0,
    ConnectionType: connectionDetails.connectionType,
    IsDisabled: connectionDetails.isDisabled,
    ShortFormId: connectionDetails.shortForm,
  };

  const pairs = modifiedConnectionPairs.map((x) => ({
    ConnectionId1: connectionId,
    ConnectionId2: x.ConnectionId,
    ConnectionPairName: x.connectionPairName,
    ConnectionPairId: x.connectionPairId,
    Status: x.status,
  }));

  //Assigning the shared catalog info to the appropriate connection pair as part of editing any details of a connectionPair
  //as well as editing both the connection pair and shared catalog settings
  if (pairs.length !== 0 && catalogSharingSetupDetails.length !== 0) {
    pairs.forEach((pair) => {
      const matchedPair = catalogSharingSetupDetails.find(
        (c) =>
          c.ConnectionId1 === pair.ConnectionId1 &&
          c.ConnectionId2 === pair.ConnectionId2
      );

      if (matchedPair) {
        pair.SharedCatalogInfo = JSON.stringify({
          PrimaryConnectionId: matchedPair.PrimaryConnectionId,
          SecondaryConnectionId: matchedPair.SecondaryConnectionId,
          SelectedCatalogs: matchedPair.SelectedCatalogs,
        });
      }
    });
  }

  //Assigning the shared catalog info to the appropriate connection pair as part of adding a new connectionPair
  //or we don't modify any other information related to the connection pair (modifiedConnectionPairs object is empty)
  if (
    connectionPairObject.length !== 0 &&
    catalogSharingSetupDetails.length !== 0 &&
    pairs.length === 0
  ) {
    pairs.push(
      ...connectionPairObject.map((obj) => ({ ...obj })) // shallow clone
    );

    pairs.forEach((pair) => {
      const matchedPair = catalogSharingSetupDetails.find(
        (c) =>
          c.ConnectionId1 === pair.ConnectionId1 &&
          c.ConnectionId2 === pair.ConnectionId2
      );

      if (matchedPair) {
        pair.SharedCatalogInfo = JSON.stringify({
          PrimaryConnectionId: matchedPair.PrimaryConnectionId,
          SecondaryConnectionId: matchedPair.SecondaryConnectionId,
          SelectedCatalogs: matchedPair.SelectedCatalogs,
        });
      }
    });
  }

  const workflowSettings = [];
  for (const id in workflowModel) {
    const workflows = workflowModel[id];
    const profiles = [];
    if (workflows && workflows.length) {
      workflows.forEach((wf) => {
        profiles.push({
          TargetConnectionId: wf.targetConnectionId,
          SourceConnectionId: wf.sourceConnectionId,
          OperationId: wf.operationId,
        });
      });
    }
    workflowSettings.push({
      TargetConnectionId: id,
      ConnectionProfiles: profiles,
    });
  }
  return {
    Connection: model,
    ConnectionPairs: pairs,
    WorkflowSettings: workflowSettings,
  };
};

export const handleEditConnection = async (
  dispatch,
  connectionId,
  connectionDetails,
  modifiedConnectionPairs,
  catalogSharingSetupDetails,
  connectionPairObject,
  workflowModel,
  endpointId,
  onClose
) => {
  // Validate if there are connection pairs marked for deletion
  if (modifiedConnectionPairs.some((pair) => pair.status === 3)) {
    const deletedPairs = modifiedConnectionPairs
      .filter((pair) => pair.status === 3)
      .map((pair) => pair.connectionPairName)
      .join(", ");

    const userConfirmed = await new Promise((resolve) => {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          title: "Delete Connection Pair Warning",
          message: `Are you sure you want to delete the following Connection Pairs? \n\n ${deletedPairs}`,
          buttonLabel: "Delete",
          onButtonClick: () => resolve(true),
          buttonLabel2: "Cancel",
          onButtonClick2: () => resolve(false),
        })
      );
    });

    if (!userConfirmed) {
      return; // Exit if the user cancels
    }
  }

  // Prepare the payload using the buildEditModel function
  const payload = buildEditModel(
    connectionId,
    connectionDetails,
    modifiedConnectionPairs,
    catalogSharingSetupDetails,
    connectionPairObject,
    workflowModel,
    endpointId
  );

  try {
    dayjs.extend(timezone);
    const response = await axios.put("/connections/edit-connection", payload, {
      params: { localTimeZone: dayjs.tz.guess() },
    });

    //we might have to update the redux variables here
    if (response && response.status === 200) {
      dispatch(setTriggerRefreshForConnectionSummary());
      dispatch(
        createAlert({
          status: "Success",
          alertType: "result",
          title: "Connection Updated!",
          message: "Connection is updated correctly.",
          buttonLabel: "Close",
          onClose: onClose,
        })
      );
      dispatch(
        clearSharedCatalogSetupDetails({
          catalogSharingSetupDetails: [],
        })
      );
      dispatch(
        clearConnectionPairObject({
          connectionPairObject: [],
        })
      );
    } else if (response && response.status === 201) {
      dispatch(setTriggerRefreshForConnectionSummary());

      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          title: "Connection Updated",
          message:
            "Update not completely successful. Partial result could be saved. " +
            response.data.Message,
          buttonLabel: "Close",
        })
      );
      dispatch(
        clearSharedCatalogSetupDetails({
          catalogSharingSetupDetails: [],
        })
      );
      dispatch(
        clearConnectionPairObject({
          connectionPairObject: [],
        })
      );
    } else {
      dispatch(
        createAlert({
          status: "Error",
          alertType: "result",
          title: "Update failed.",
          message:
            "Update not completely successfully, partial result could be saved. Please refresh and try again.",
          buttonLabel: "Close",
        })
      );
      dispatch(
        clearSharedCatalogSetupDetails({
          catalogSharingSetupDetails: [],
        })
      );
      dispatch(
        clearConnectionPairObject({
          connectionPairObject: [],
        })
      );
    }
  } catch (error) {
    console.error("Error updating connection:", error);
    dispatch(
      createAlert({
        status: "Error",
        alertType: "result",
        title: "Update failed.",
        message: "An error occurred while updating the connection.",
        buttonLabel: "Close",
      })
    );
    dispatch(
      clearSharedCatalogSetupDetails({
        catalogSharingSetupDetails: [],
      })
    );
    dispatch(
      clearConnectionPairObject({
        connectionPairObject: [],
      })
    );
  }
};

export const saveNewConnection = async (
  connectionDetails,
  endpointId,
  schemaCollectionId = null,
  onSuccess,
  onError
) => {
  const jsonProperties = JSON.stringify({
    ShortFormId: connectionDetails.shortForm,
    DisplayName: connectionDetails.displayName,
    ConnectionType: connectionDetails.connectionType,
  });

  try {
    const response = await axios.post(
      "/connections",
      JSON.stringify({
        connectionId: -1,
        connectionName: connectionDetails.connectionName,
        JSONProperties: jsonProperties,
        endpointId: endpointId ?? 0,
        schemaCollectionId: schemaCollectionId,
        isDisabled: connectionDetails.isDisabled,
      })
    );

    if (response?.data) {
      if (onSuccess) onSuccess(response.data);
      return response.data; // Return the new connection ID
    } else {
      throw new Error("No data in response");
    }
  } catch (error) {
    console.error("Error saving new connection:", error);
    if (onError) onError(error);
    throw error;
  }
};

export const validateForm = (
  tabErrors,
  setTabValue,
  dispatch,
  setShowErrors,
  setConnectionTabValue
) => {
  if (Object.values(tabErrors).some((error) => error)) {
    setShowErrors(true);

    const firstErrorTab = Object.keys(tabErrors).find((key) => tabErrors[key]);
    const tabIndex = {
      connectionDetails: 0,
      endpoints: 0,
      schema: 1,
      connectionPair: 2,
      workflows: 3,
    }[firstErrorTab];

    setTabValue(tabIndex);

    if (tabIndex === 0 && setConnectionTabValue) {
      setConnectionTabValue(0);
    }

    dispatch(
      createAlert({
        status: "Error",
        alertType: "result",
        title: "Validation Error",
        message: "Please fix the errors in the form before saving.",
        buttonLabel: "Close",
      })
    );

    return false;
  }

  return true;
};

export const getEndpointSchema = (dispatch, connectionId) => {
  axios
    .get("/template/schema-collection", {
      params: {
        connectionId: connectionId,
      },
    })
    .then((response) => {
      if (response.data == null || response.data == "") {
        const schemaCollectionInfo = {
          SchemaCollectionId: -1,
          connectionId,
        };

        dispatch(
          setSchemaCollectionAdded({
            schemaCollectionAdded: schemaCollectionInfo,
          })
        );
      } else {
        var schemaData = response.data;
        const schemaTypeName =
          Object.keys(SchemaType).find(
            (key) => SchemaType[key] === schemaData.SchemaType
          ) || "Unknown";
        const schemaCollectionInfo = {
          SchemaCollectionId: schemaData.SchemaCollectionId,
          connectionId,
          SchemaTypeName: schemaTypeName,
          SchemaCollectionName: schemaData.SchemaCollectionName,
          SchemaType: schemaData.SchemaType,
        };
        dispatch(
          setSchemaCollectionAdded({
            schemaCollectionAdded: schemaCollectionInfo,
          })
        );
      }
    })
    .catch((error) => {
      console.error("Error fetching data: ", error);
    });
};

export const EndpointType = {
  RetailChannel: 0,
  ECommerce: 1,
  ERP: 2,
  WMS: 3,
  CMS: 4,
};

export const SchemaType = {
  X12EDI: 1,
  JSON: 2,
  FlatFile: 3,
  XML: 4,
  CSV: 5,
  ProprietaryDataStream: 6,
};
